import React, { useState, useEffect } from 'react';
import { SummaryRenderer } from './SummaryRenderer';

function ReportsTab({ user }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaries, setSummaries] = useState({});
  const [summaryLoading, setSummaryLoading] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState('');
  const [shareModal, setShareModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [openFeedback, setOpenFeedback] = useState(null);

  const handleDelete = async (reportId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/${reportId}`, { method: 'DELETE' });
      if (res.ok) {
        setReports(prev => prev.filter(r => r.id !== reportId));
        setDeleteConfirm(null);
      } else { alert('Failed to delete report.'); }
    } catch { alert('Cannot connect to server.'); }
  };

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/${user.id}`);
        setReports(await res.json());
      } catch { }
      setLoading(false);
    };
    fetchReports();
  }, [user.id]);

  const handleSummarize = async (reportId) => {
    setSummaryLoading(prev => ({ ...prev, [reportId]: true }));
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AI/summary-report/${reportId}`);
      const data = await res.json();
      setSummaries(prev => ({ ...prev, [reportId]: data.summary || 'No summary available.' }));
    } catch { setSummaries(prev => ({ ...prev, [reportId]: 'Cannot connect to AI service.' })); }
    setSummaryLoading(prev => ({ ...prev, [reportId]: false }));
  };

  const handleSave = async (report) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/download/${report.id}`);
      if (!res.ok) { alert('Cannot save this report.'); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = report.fileName || `report-${report.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch { alert('Save failed. Check your connection.'); }
  };

  const handleSaveWithSummary = async (report) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/download/${report.id}`);
      if (!res.ok) { alert('Cannot save this report.'); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = report.fileName || `report-${report.id}`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(a.href);

      const summaryContent = summaries[report.id] || 'No AI summary generated yet.';
      const date = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
      const summaryText = [
        'MYHEALTH JOURNAL — AI REPORT SUMMARY', '=====================================',
        `Report Name : ${report.fileName}`, `Report Type : ${report.reportType}`,
        `Report Date : ${new Date(report.uploadDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`,
        `Generated On: ${date}`, '=====================================', '', 'AI SUMMARY', '----------',
        summaryContent, '', '=====================================', '⚠️ DISCLAIMER', '----------',
        'This summary is generated by MyHealth Journal AI based ONLY on the',
        'reports you have uploaded. This is NOT medical advice or a prescription.',
        'Do not use this as a substitute for professional medical consultation.',
        'Please consult your doctor for any health decisions.',
        '=====================================', 'Generated by MyHealth Journal'
      ].join('\n');

      const summaryBlob = new Blob([summaryText], { type: 'text/plain' });
      const sa = document.createElement('a');
      sa.href = URL.createObjectURL(summaryBlob);
      sa.download = `AI_Summary_${report.fileName?.replace(/\.[^/.]+$/, '') || 'report'}.txt`;
      document.body.appendChild(sa); sa.click();
      document.body.removeChild(sa); URL.revokeObjectURL(sa.href);
    } catch { alert('Save failed.'); }
  };

  const handleSendEmail = (report) => {
    const subject = encodeURIComponent(`Medical Report + AI Summary — ${report.reportType} — ${new Date(report.uploadDate).toLocaleDateString('en-IN')}`);
    const summary = summaries[report.id] || 'No AI summary generated yet.';
    const body = encodeURIComponent(`Hello,\n\nI am sharing my medical report and AI summary from MyHealth Journal.\n\nREPORT DETAILS\n--------------\nFile: ${report.fileName}\nType: ${report.reportType}\nDate: ${new Date(report.uploadDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}\n\nReport Link: ${process.env.REACT_APP_API_URL}/api/Reports/download/${report.id}\n\nAI SUMMARY\n----------\n${summary}\n\n⚠️ DISCLAIMER\n----------\nThis summary is generated by MyHealth Journal AI based ONLY on the uploaded report. This is NOT medical advice or a prescription. Please consult your doctor for any health decisions.\n\nGenerated by MyHealth Journal`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const filtered = reports.filter(r =>
    r.fileName?.toLowerCase().includes(search.toLowerCase()) ||
    r.reportType?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="px-4 py-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-black text-[#191c1e]">My Reports</h2>
          <p className="text-sm text-gray-400 mt-0.5">Health history & lab reports</p>
        </div>
        <button onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-2 px-5 py-3 bg-[#0d2f8f] text-white font-bold rounded-2xl text-sm shadow-lg active:scale-95 transition-all">
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>upload</span>Upload
        </button>
      </div>

      {showUpload && <UploadReportForm user={user} onClose={() => { setShowUpload(false); const fetchReports = async () => { try { const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/${user.id}`); setReports(await res.json()); } catch { } }; fetchReports(); }} />}

      <div className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3.5 border border-gray-200 shadow-sm mb-4">
        <span className="material-symbols-outlined text-gray-400" style={{ fontSize: '20px' }}>search</span>
        <input type="text" placeholder="Search reports..." className="flex-1 bg-transparent border-none outline-none text-base placeholder-gray-400"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading && <p className="text-center text-gray-400 text-sm py-8">Loading...</p>}

      <div className="space-y-3">
        {filtered.map(report => (
          <div key={report.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4 flex items-center gap-3">
              <div className="w-12 h-12 bg-[#003d9b]/10 rounded-xl flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[#003d9b]" style={{ fontSize: '22px' }}>description</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#191c1e] text-sm truncate">{report.fileName}</p>
                <p className="text-xs text-gray-400 mt-0.5">{report.reportType} • {new Date(report.uploadDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                <span className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full ${report.extractedText ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                  {report.extractedText ? '✅ Processed' : '⏳ Processing'}
                </span>
              </div>
              <button onClick={() => setDeleteConfirm(report)}
                className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-full transition-colors shrink-0">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
              </button>
            </div>

            <div className="px-4 pb-2 grid grid-cols-4 gap-1.5">
              <button onClick={() => window.open(`${process.env.REACT_APP_API_URL}/api/Reports/view/${report.id}`, '_blank')}
                className="flex items-center justify-center gap-1 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-xl text-[10px] active:scale-95">
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>open_in_new</span>View
              </button>
              <button onClick={() => handleSave(report)}
                className="flex items-center justify-center gap-1 py-2.5 bg-[#003d9b] text-white font-bold rounded-xl text-[10px] active:scale-95">
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>save</span>Save
              </button>
              <button onClick={() => setShareModal(shareModal === report.id ? null : report.id)}
                className="flex items-center justify-center gap-1 py-2.5 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl text-[10px] active:scale-95">
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>more_horiz</span>More
              </button>
              <button onClick={() => setOpenFeedback(openFeedback === report.id ? null : report.id)}
                className={`flex items-center justify-center gap-1 py-2.5 font-bold rounded-xl text-[10px] active:scale-95 border ${openFeedback === report.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-700 border-blue-100'} ${report.doctorFeedbacks?.length > 0 ? 'ring-2 ring-blue-300' : ''}`}>
                <span className="material-symbols-outlined" style={{ fontSize: '14px', fontVariationSettings: '"FILL" 1' }}>rate_review</span>
                {report.doctorFeedbacks?.length > 0 ? `Dr.(${report.doctorFeedbacks.length})` : 'Dr.'}
              </button>
            </div>

            {openFeedback === report.id && (
              <div className="px-4 pb-3">
                <div className="bg-blue-50 rounded-2xl p-3 border border-blue-100 space-y-2">
                  <p className="text-xs font-black text-blue-700 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: '"FILL" 1' }}>rate_review</span>
                    Doctor's Feedback
                  </p>
                  {report.doctorFeedbacks && report.doctorFeedbacks.length > 0 ? (
                    <div className="space-y-2">
                      {report.doctorFeedbacks.map((fb, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-2.5 border border-blue-100">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="material-symbols-outlined text-blue-500 text-xs" style={{ fontVariationSettings: '"FILL" 1' }}>person</span>
                            <p className="text-[10px] font-black text-blue-700">Dr. {fb.doctorName}</p>
                            <p className="text-[10px] text-blue-400 ml-auto">{new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed">{fb.feedback}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 text-center py-2">No feedback from doctor yet</p>
                  )}
                </div>
              </div>
            )}

            {shareModal === report.id && (
              <div className="px-4 pb-3">
                <div className="bg-gray-50 rounded-2xl p-3 space-y-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Choose Option</p>
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
                    <p className="text-xs font-black text-[#0D1B2A] mb-2">📄 Option 1 — Report Only</p>
                    <div className="flex gap-2">
                      <button onClick={() => { handleSave(report); setShareModal(null); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#003d9b] text-white font-bold rounded-xl text-xs active:scale-95">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>save</span>Gallery
                      </button>
                      <button onClick={() => {
                        const subject = encodeURIComponent(`Medical Report — ${report.reportType}`);
                        const body = encodeURIComponent(`Hello,\n\nPlease find my medical report below.\n\nReport: ${report.fileName}\nType: ${report.reportType}\nDate: ${new Date(report.uploadDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}\n\nReport Link: ${process.env.REACT_APP_API_URL}/api/Reports/download/${report.id}\n\nSent via MyHealth Journal`);
                        window.location.href = `mailto:?subject=${subject}&body=${body}`; setShareModal(null);
                      }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-blue-50 text-blue-600 font-bold rounded-xl text-xs active:scale-95 border border-blue-200">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>mail</span>Mail
                      </button>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
                    <p className="text-xs font-black text-[#0D1B2A] mb-2">🤖 Option 2 — Report + AI Summary</p>
                    <div className="flex gap-2">
                      <button onClick={() => { handleSaveWithSummary(report); setShareModal(null); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-purple-600 text-white font-bold rounded-xl text-xs active:scale-95">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>save</span>Gallery
                      </button>
                      <button onClick={() => { handleSendEmail(report); setShareModal(null); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-purple-50 text-purple-700 font-bold rounded-xl text-xs active:scale-95 border border-purple-200">
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>mail</span>Mail
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {report.extractedText && !summaries[report.id] && (
              <div className="px-4 pb-4">
                <button onClick={() => handleSummarize(report.id)} disabled={summaryLoading[report.id]}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-purple-50 border border-purple-200 text-purple-700 font-bold rounded-xl text-xs active:scale-95 transition-all disabled:opacity-50">
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>auto_awesome</span>
                  {summaryLoading[report.id] ? 'Reading...' : 'Get AI Summary'}
                </button>
              </div>
            )}

            {summaries[report.id] && (
              <div className="px-4 pb-4">
                <div className="bg-purple-50 rounded-xl p-3 border-l-4 border-purple-400">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-black text-purple-700">✨ AI SUMMARY</p>
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] text-purple-400">{new Date(report.uploadDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      <button onClick={() => setSummaries(prev => { const n = {...prev}; delete n[report.id]; return n; })}
                        className="w-5 h-5 bg-purple-200 rounded-full flex items-center justify-center hover:bg-purple-300 active:scale-95">
                        <span className="material-symbols-outlined text-purple-700" style={{ fontSize: '12px' }}>close</span>
                      </button>
                    </div>
                  </div>
                  <SummaryRenderer text={summaries[report.id]} />
                  <p className="text-[10px] text-amber-600 mt-2 leading-relaxed border-t border-purple-200 pt-2">
                    ⚠️ Not medical advice. Consult your doctor for health decisions.
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && !loading && (
          <div className="bg-white rounded-3xl px-6 py-10 text-center border border-gray-100 shadow-sm">
            <div className="relative w-40 h-40 mx-auto mb-2 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-gray-50" />
              <span className="absolute rounded-full" style={{ top: '18px', right: '14px', width: '8px', height: '8px', background: '#7fd6c4' }} />
              <span className="absolute rounded-full" style={{ bottom: '28px', left: '20px', width: '9px', height: '9px', background: '#c9c6f5' }} />
              <span className="material-symbols-outlined absolute text-purple-200" style={{ top: '48px', left: '8px', fontSize: '18px' }}>add</span>
              <span className="material-symbols-outlined absolute text-purple-200" style={{ bottom: '46px', right: '6px', fontSize: '16px' }}>add</span>
              <svg width="86" height="96" viewBox="0 0 86 96" className="relative z-10" style={{ filter: 'drop-shadow(0 10px 14px rgba(60,70,150,0.15))' }}>
                <rect x="4" y="10" width="78" height="82" rx="10" fill="#ffffff" stroke="#c7cdf0" strokeWidth="2" />
                <rect x="28" y="2" width="30" height="16" rx="5" fill="#3446e0" />
                <circle cx="43" cy="10" r="3" fill="#ffffff" />
                <rect x="16" y="28" width="20" height="4" rx="2" fill="#3446e0" />
                <rect x="16" y="40" width="54" height="3" rx="1.5" fill="#dfe2f5" />
                <rect x="16" y="49" width="54" height="3" rx="1.5" fill="#dfe2f5" />
                <rect x="16" y="58" width="54" height="3" rx="1.5" fill="#dfe2f5" />
                <rect x="16" y="67" width="38" height="3" rx="1.5" fill="#dfe2f5" />
              </svg>
            </div>
            <p className="text-[#191c1e] font-black text-2xl mb-2">No reports yet</p>
            <p className="text-gray-400 text-base mb-7 leading-snug">Upload your health reports to keep<br />all your records in one place.</p>
            <button onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0d2f8f] text-white font-bold rounded-full text-base active:scale-95 transition-all">
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>upload</span>Upload Report
            </button>
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-end">
          <div className="w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-3">
                <span className="material-symbols-outlined text-red-500 text-3xl" style={{ fontVariationSettings: '"FILL" 1' }}>delete</span>
              </div>
              <h3 className="font-black text-[#191c1e] text-lg">Delete Report?</h3>
              <p className="text-gray-400 text-sm mt-1">"{deleteConfirm.fileName}" will be permanently deleted.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-2xl text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════
// UPLOAD REPORT FORM
// ═══════════════════════════════
function UploadReportForm({ user, onClose }) {
  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [detected, setDetected] = useState(null);
  // ✅ Added reportName field
  const [form, setForm] = useState({ reportType: 'Blood Test', hospitalName: '', prescriptionName: '', reportName: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleFileSelect = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setStep('detecting');
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await fetch(`${process.env.REACT_APP_AI_URL}/detect-type`, { method: 'POST', body: formData });
      const data = await res.json();
      setDetected(data);
      // ✅ Pre-fill both name fields with file name
      setForm(prev => ({ ...prev, prescriptionName: f.name, reportName: f.name }));
    } catch {
      setDetected({ type: 'report', has_medicines: false, has_lab_results: true });
    }
    setStep('questions');
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);

    // ✅ Use correct name based on document type
    const customName = detected?.type === 'prescription'
      ? (form.prescriptionName || file.name)
      : detected?.type === 'both'
      ? (form.reportName || form.prescriptionName || file.name)
      : (form.reportName || file.name);

    const formData = new FormData();
    formData.append('PatientId', user.id);
    formData.append('ReportType', form.reportType);
    formData.append('HospitalName', form.hospitalName);
    formData.append('Name', customName);
    formData.append('File', file);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (res.ok) { setMessage('✅ ' + data.message); setTimeout(onClose, 2000); }
      else setMessage(`❌ ${data.message}`);
    } catch { setMessage('❌ Cannot connect to server.'); }
    setLoading(false);
  };

  const typeConfig = {
    report: { icon: 'science', color: '#3446e0', bg: '#e9edfb', label: 'Detected as Lab Report!', desc: 'Will be saved to your Reports tab' },
    prescription: { icon: 'medication', color: '#3446e0', bg: '#e9edfb', label: 'Detected as Prescription!', desc: 'Medicines will be extracted and saved to Meds tab' },
    both: { icon: 'note_add', color: '#3446e0', bg: '#e9edfb', label: 'Detected as Report & Prescription!', desc: 'Saved to both Reports and Meds tabs' },
  };
  const config = detected ? (typeConfig[detected.type] || typeConfig.report) : typeConfig.report;

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-5 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-black text-[#191c1e] text-xl">Upload Document</h3>
        {step !== 'select' && (
          <button onClick={() => { setStep('select'); setFile(null); setDetected(null); setMessage(''); }}
            className="text-[#2f5fff] font-bold text-base">Change file</button>
        )}
      </div>
      {step === 'select' && <p className="text-gray-400 text-sm mb-4">AI will auto-detect report or prescription</p>}
      {message && (
        <div className={`p-2 rounded-xl text-xs mb-3 ${message.includes('✅') ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{message}</div>
      )}

      {step === 'select' && (
        <div className="space-y-3">
          <label className="w-full border border-gray-100 bg-white rounded-2xl p-4 flex items-center gap-4 cursor-pointer active:scale-[0.99] transition-all">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: '#0d2f8f' }}>
              <span className="material-symbols-outlined text-white" style={{ fontSize: '22px', fontVariationSettings: '"FILL" 1' }}>photo_camera</span>
            </div>
            <div className="flex-1">
              <p className="font-extrabold text-[#191c1e] text-base">Take Photo</p>
              <p className="text-sm text-gray-400 mt-0.5">Use camera to capture</p>
            </div>
            <span className="material-symbols-outlined text-[#0d2f8f]" style={{ fontSize: '22px' }}>chevron_right</span>
            <input type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
          </label>
          <label className="w-full border border-gray-100 bg-white rounded-2xl p-4 flex items-center gap-4 cursor-pointer active:scale-[0.99] transition-all">
            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-gray-600" style={{ fontSize: '22px' }}>image</span>
            </div>
            <div className="flex-1">
              <p className="font-extrabold text-[#191c1e] text-base">Choose from Gallery</p>
              <p className="text-sm text-gray-400 mt-0.5">Select photo from device</p>
            </div>
            <span className="material-symbols-outlined text-[#0d2f8f]" style={{ fontSize: '22px' }}>chevron_right</span>
            <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
          </label>
          <label className="w-full border border-gray-100 bg-white rounded-2xl p-4 flex items-center gap-4 cursor-pointer active:scale-[0.99] transition-all">
            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-gray-600" style={{ fontSize: '22px' }}>upload_file</span>
            </div>
            <div className="flex-1">
              <p className="font-extrabold text-[#191c1e] text-base">Upload File</p>
              <p className="text-sm text-gray-400 mt-0.5">PDF, JPG, PNG from files</p>
            </div>
            <span className="material-symbols-outlined text-[#0d2f8f]" style={{ fontSize: '22px' }}>chevron_right</span>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} className="hidden" />
          </label>
        </div>
      )}

      {step === 'detecting' && (
        <div className="flex flex-col items-center py-5 space-y-3">
          <span className="material-symbols-outlined text-[#003d9b] text-4xl animate-pulse" style={{ fontVariationSettings: '"FILL" 1' }}>auto_awesome</span>
          <p className="font-bold text-[#191c1e] text-sm">AI is reading your document...</p>
          <div className="flex gap-1">
            {[0, 0.15, 0.3].map((d, i) => (
              <div key={i} className="w-2 h-2 bg-[#003d9b] rounded-full animate-bounce" style={{ animationDelay: `${d}s` }}></div>
            ))}
          </div>
        </div>
      )}

      {step === 'questions' && detected && (
        <div className="space-y-4">
          <div className="rounded-2xl p-4 flex items-center gap-4" style={{ background: config.bg }}>
            <div className="w-11 h-11 rounded-xl bg-white/70 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined" style={{ fontSize: '22px', color: config.color }}>{config.icon}</span>
            </div>
            <div>
              <p className="font-black text-[#191c1e] text-base">{config.label}</p>
              <p className="text-sm text-gray-500 mt-0.5">{config.desc}</p>
            </div>
          </div>

          {(detected.type === 'report' || detected.type === 'both') && (
            <>
              {/* ✅ Report Name input */}
              <div className="flex items-center gap-4 px-4 py-3.5 border border-gray-200 rounded-2xl bg-white">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#e9edfb' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#3446e0' }}>edit</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Report Name</p>
                  <input type="text" placeholder="e.g. Blood Test Jan 2026"
                    value={form.reportName} onChange={e => setForm({ ...form, reportName: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-base text-[#191c1e] placeholder-gray-400" />
                </div>
              </div>
              <div className="relative flex items-center gap-4 px-4 py-3.5 border border-gray-200 rounded-2xl bg-white">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#e9edfb' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#3446e0' }}>science</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Report Type</p>
                  <select value={form.reportType} onChange={e => setForm({ ...form, reportType: e.target.value })}
                    className="w-full bg-transparent border-none outline-none font-bold text-lg text-[#191c1e] appearance-none pr-6">
                    {['Blood Test', 'Urine Test', 'X-Ray', 'MRI', 'CT Scan', 'ECG', 'Other'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <span className="material-symbols-outlined text-[#191c1e] absolute right-4 pointer-events-none" style={{ fontSize: '22px' }}>expand_more</span>
              </div>
              <div className="flex items-center gap-4 px-4 py-3.5 border border-gray-200 rounded-2xl bg-white">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#e9edfb' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#3446e0' }}>account_balance</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Hospital/Lab Name (optional)</p>
                  <input type="text" placeholder="Enter hospital or lab name"
                    value={form.hospitalName} onChange={e => setForm({ ...form, hospitalName: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-base text-[#191c1e] placeholder-gray-400" />
                </div>
              </div>
            </>
          )}

          {(detected.type === 'prescription' || detected.type === 'both') && (
            <div className="flex items-center gap-4 px-4 py-3.5 border border-gray-200 rounded-2xl bg-white">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#e9edfb' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#3446e0' }}>pill</span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400">Prescription Name</p>
                <input type="text" placeholder="e.g. Dr. Sharma's Prescription"
                  value={form.prescriptionName} onChange={e => setForm({ ...form, prescriptionName: e.target.value })}
                  className="w-full bg-transparent border-none outline-none text-base text-[#191c1e] placeholder-gray-400" />
              </div>
            </div>
          )}

          <button onClick={handleUpload} disabled={loading}
            className="w-full py-4 bg-[#0d2f8f] text-white font-bold rounded-2xl text-base flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? 'Saving...' : <><span className="material-symbols-outlined" style={{ fontSize: '20px' }}>save</span>Save Document</>}
          </button>
        </div>
      )}
    </div>
  );
}

export default ReportsTab;